/**
 * "Add a training to this version" — a training PDF, a training video, or a
 * link to a video hosted elsewhere (CEO, 2026-09-17).
 *
 * A TRAINING IS AN ORDINARY DOCUMENT WITH `purpose: "training"`. One model,
 * one resolver, one audited upload path — the same reasoning that made release
 * documentation ordinary Documents. What the purpose changes is where it is
 * SHOWN: the version pane's Trainings section and the reseller portal's
 * Trainings page, never among release documentation or library documents, and
 * never to a customer. portal-api decides all of that; this dialog only files
 * the record correctly.
 *
 * Two ways in — a file of ANY size (uploads have no cap since 2026-10-31; the
 * proxy streams the body via disk, see docs/documentation.md → "No upload size
 * limit"), or a link to a video that already lives on YouTube / Vimeo:
 *
 * - **A file** — `.pdf` goes up as doc_type `guide`, `video/*` as doc_type
 *   `video` (portal-api only emits a playable video payload for a `video`
 *   document), through `uploadDoc` with the version pinned.
 * - **A link** — YouTube, Vimeo or any other address, created through the
 *   authored path (`createDoc`) as doc_type `video` with `external_url` and
 *   the player kind detected from the address.
 *
 * Either way: pinned to the version (`default_release_id`), audience
 * `release` with the version in `release_ids` (a pinned document must never
 * stay `general`), no folder, published unless switched off.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link2, Upload } from "lucide-react";
import { useEffect, useRef, useState, type JSX } from "react";

import { createDoc, uploadDoc } from "../../../pages/documentation/api.js";
import { deriveKindFromUrl } from "../../../pages/documentation/VideoFieldsEditor.js";

type Source = "file" | "link";

/** What a picked file becomes, or `null` when it is not training material.
 *  A PDF is filed as a guide; any video as `video`, so it plays. */
export function trainingDocTypeForFile(file: Pick<File, "name" | "type">): "guide" | "video" | null {
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) return "guide";
  return null;
}

/** An address a player can use: absolute http(s) only. */
export function isVideoLink(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** The player kind for a pasted link. `upload` never comes from a link. */
export function trainingVideoKind(url: string): "youtube" | "vimeo" | "embed" {
  const kind = deriveKindFromUrl(url.trim());
  return kind === "upload" ? "embed" : kind;
}

interface Props {
  releaseId: number;
  version: string;
  open: boolean;
  onClose: () => void;
}

export function ReleaseTrainingDialog({ releaseId, version, open, onClose }: Props): JSX.Element {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<Source>("file");
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState("");
  const [title, setTitle] = useState("");
  const [publish, setPublish] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reset per opening so a second training never inherits the first's file.
  useEffect(() => {
    if (!open) return;
    setSource("file");
    setFile(null);
    setLink("");
    setTitle("");
    setPublish(true);
    setError(null);
  }, [open]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const status = publish ? "published" : "draft";
      if (source === "file") {
        if (!file) throw new Error("Pick a PDF or a video to upload.");
        const docType = trainingDocTypeForFile(file);
        if (!docType) throw new Error("A training is a PDF or a video.");
        return uploadDoc({
          file,
          ...(title.trim() ? { title: title.trim() } : {}),
          status,
          // No folder, deliberately: a training belongs to the version.
          default_release_id: String(releaseId),
          doc_type: docType,
          purpose: "training",
          access_scope: "release",
          release_ids: [releaseId],
        });
      }
      if (!title.trim()) throw new Error("Give the training a title.");
      if (!isVideoLink(link)) throw new Error("Paste the full video address, starting with https://.");
      const url = link.trim();
      return createDoc({
        document: {
          title: title.trim(),
          status,
          doc_type: "video",
          default_release_id: releaseId,
          external_url: url,
          purpose: "training",
        },
        metadata: { video: { kind: trainingVideoKind(url) } },
        access_scope: "release",
        release_ids: [releaseId],
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "release-trainings", releaseId] });
      void qc.invalidateQueries({ queryKey: ["docs"] });
      onClose();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Could not add the training."),
  });

  const ready = source === "file" ? Boolean(file) : Boolean(link.trim()) && Boolean(title.trim());
  const canSubmit = ready && !saveMut.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a training to release {version}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
          A training PDF or video for {version}. Resellers find it on their Trainings page. It is
          not listed with this version&apos;s documentation, and customers never see it.
        </DialogContentText>

        <Box sx={{ display: "grid", gap: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={source}
            onChange={(_, next: Source | null) => {
              if (next) {
                setSource(next);
                setError(null);
              }
            }}
            aria-label="Training source"
          >
            <ToggleButton value="file">
              <Upload size={14} style={{ marginRight: 6 }} />
              Upload a file
            </ToggleButton>
            <ToggleButton value="link">
              <Link2 size={14} style={{ marginRight: 6 }} />
              Link a video
            </ToggleButton>
          </ToggleButtonGroup>

          {source === "file" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<Upload size={14} />} onClick={() => fileInput.current?.click()}>
                {file ? "Change file" : "Choose file"}
              </Button>
              <input
                ref={fileInput}
                type="file"
                hidden
                data-testid="release-training-file"
                aria-label="Training file"
                accept=".pdf,application/pdf,video/*"
                onChange={(e) => {
                  const picked = e.target.files?.[0] ?? null;
                  setFile(picked);
                  setError(null);
                  if (picked && !title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ""));
                  e.target.value = "";
                }}
              />
              <Typography sx={{ fontSize: 13, color: file ? "text.primary" : "text.disabled" }}>
                {file ? file.name : "A PDF or a video, any size."}
              </Typography>
            </Box>
          ) : (
            <TextField
              size="small"
              label="Video link"
              fullWidth
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              helperText="YouTube, Vimeo, or any address the video plays from."
              inputProps={{ "data-testid": "release-training-link" }}
            />
          )}

          <TextField
            size="small"
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`e.g. ${version} Administrator Training`}
          />

          <FormControlLabel
            control={<Switch checked={publish} onChange={(e) => setPublish(e.target.checked)} />}
            label={publish ? "Publish now" : "Save as draft (staff only)"}
          />

          {error && (
            <Alert severity="error" sx={{ fontSize: 13 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Saving…" : "Add training"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
